import React, { Component } from 'react';
import { Text, View, Image, Pressable, StyleSheet } from 'react-native';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { OPComponent, I18n, Utils, Navigation, NavBar, CTAButton } from '../components';
import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../constants/styles';

import { login, requestResetMFA, resetState } from './actions';
import CommonStyles from './common_styles';
import StyledInput from './styled_input';
import AuthService from '../../services/auth_service.js';

import { PENDING, REQUESTING, SUCCESS, FAILED } from '../../actions/statusTypes';

import { ErrorView, AuthForm } from './auth_views';

const passwordInputRef = React.createRef();

class Login extends OPComponent {
  static navigatorStyle = NavBar.authNavbarStyle;
  static statusBarStyle = NavBar.authStatusBarStyle;

  constructor(props) {
    super(props);

    this.state = {
      email: props.email || null,
      password: null,
      mfaToken: null,
      passwordHidden: true,
      validationErrorMessage: null,
    };
  }

  componentDidMount() {
    Utils.trackScreen('Auth', 'Login');

    Navigation.registerForButtonEvents(this);

    AuthService.getStoredCreds()
      .then((credentials) => {
        if (credentials && credentials.username && credentials.password) {
          if (!this.props.email || this.props.email === credentials.username) {
            this.setState({
              email: credentials.username,
              password: credentials.password,
            });
          }
        }
      })
      .catch((e) => {});
  }

  componentWillUnmount() {
    Navigation.unRegisterForButtonEvents(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props.email && !prevProps.email) {
      this.setState({
        email: this.props.email,
      });
    }
  }

  // StyledInput is not a "controlled" component so we probably dont need to be updating
  // state here when the value changes
  handleCodeChange = (mfaToken) => this.setState({ mfaToken });
  handleEmailChange = (email) => this.setState({ email });
  handlePasswordChange = (password) => this.setState({ password });
  handleEmailSubmit = () => passwordInputRef.current?.focus?.();
  handleSecureTextButtonPress = () => this.setState({ passwordHidden: !this.state.passwordHidden });

  errorMessage() {
    // return "testing error message"
    const submitError =
      this.props.errorMessage?.message ||
      (this.props.authStatus === FAILED ? I18n.t('genericErrorMessage') : '');
    return this.state.validationErrorMessage || submitError;
  }

  submit = () => {
    if (!Utils.validate.email(this.state.email)) {
      this.setState({ validationErrorMessage: I18n.t('invalidEmail') });
      return;
    }
    if (!this.state.password?.trim()) {
      this.setState({ validationErrorMessage: I18n.t('auth.enterPassword') });
      return;
    }
    if (this.state.validationErrorMessage) this.setState({ validationErrorMessage: null });

    Utils.trackEvent('Action', 'Tap', 'Login');

    this.props.actions.login(
      this.state.email.trim(),
      this.state.password.trim(),
      this.state.mfaToken?.trim()
    );
  };

  requestResetMFA = () => {
    this.props.actions.requestResetMFA(this.state.email, this.state.password);
  };

  goToResetPassword = () => {
    Navigation.openScreen(
      {
        screen: 'ForgotPassword',
        title: '',
        passProps: { military: this.props.military },
      },
      this.props.componentId
    );
  };
  renderResetPasswordBtn() {
    return (
      <Pressable style={styles.resetPasswordButton} onPress={this.goToResetPassword}>
        <Text style={styles.resetPasswordButtonText}>{I18n.t('auth.forgotPassword')}</Text>
      </Pressable>
    );
  }

  renderLogin() {
    const props = this.props;
    if (props.needsMFASetup) return null;
    if (props.needsMFACode) return null;

    return (
      <View style={[styles.inputsContainer]}>
        <ErrorView {...props} errorMessage={this.errorMessage()} />

        <StyledInput
          label={I18n.ac('form.emailAddress')}
          value={this.state.email}
          onChangeText={this.handleEmailChange}
          onSubmitEditing={this.handleEmailSubmit}
          autoComplete={'email'}
          keyboardType="email-address"
          returnKeyType="next"
          testID="email"
        />

        <StyledInput
          ref={passwordInputRef}
          label={I18n.ac('auth.password')}
          value={this.state.password}
          secureTextEntry={!!this.state.passwordHidden}
          onChangeText={this.handlePasswordChange}
          onSubmitEditing={this.submit}
          onSecureTextButtonPress={this.handleSecureTextButtonPress}
          autoComplete={'password'}
          returnKeyType="go"
          testID="password"
        />

        {__WEB__ ? (
          <View style={{ flexDirection: 'row' }}>
            <View style={{ flex: 0.5 }}>
              <CTAButton
                onPress={this.submit}
                label={I18n.t('auth.login')}
                styleType="main"
                style={{ width: 118 }}
              />
            </View>
            <View style={{ flex: 0.5 }}>{this.renderResetPasswordBtn()}</View>
          </View>
        ) : (
          <View>
            {this.renderResetPasswordBtn()}

            <Pressable style={styles.button} onPress={this.submit}>
              <Text style={styles.buttonText}>{I18n.t('auth.login')}</Text>
            </Pressable>
          </View>
        )}
      </View>
    );
  }

  render() {
    const props = this.props;
    return (
      <AuthForm
        {...props}
        errorMessage={this.errorMessage()}
        mfaToken={this.state.mfaToken}
        onSubmitMFA={this.submit}
        handleCodeChange={this.handleCodeChange}
        handleReset2FAPress={this.requestResetMFA}
        showActivityOverlay={props.authStatus === REQUESTING}
        showPrivacy={true}
        email={this.state.email}
        password={this.state.password}
      >
        {this.renderLogin()}
      </AuthForm>
    );
  }
}

const styles = StyleSheet.create({
  ...CommonStyles,
  resetPasswordButton: {
    paddingVertical: 15,
    alignSelf: 'flex-start',
  },
  resetPasswordButtonText: __WEB__
    ? {
        ...FONT.light,
        fontSize: FONT_SIZE.small,
        color: COLOR.callToAction,
      }
    : {
        ...FONT.medium,
        fontSize: FONT_SIZE.large,
        color: COLOR.callToAction,
      },
});

function mapStateToProps(state, ownProps) {
  return {
    authStatus: state.screens.auth.status,
    errorMessage: state.screens.auth.errorMessage,
    successMessage: state.screens.auth.successMessage,
    email: state.application.authFormPrefills?.email,
    needsMFASetup: state.screens.auth.needsMFASetup,
    needsMFACode: state.screens.auth.needsMFACode,
    qrcode: state.screens.auth.qrcode,
    secret: state.screens.auth.secret,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ login, requestResetMFA, resetState }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Login);
